import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, Grid, Card, CardContent,
  CardActions, Button, Chip, Stack, Alert, CircularProgress, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Divider, Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  CloudUpload as UploadIcon,
  MenuBook as BookIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CATEGORY_ORDER = [
  'Standards & Basics',
  'AV & Networking',
  'Lighting & Power',
  'Security',
  'General',
];

const CATEGORY_COLORS = {
  'Standards & Basics': '#1565C0',
  'AV & Networking':    '#2E7D32',
  'Lighting & Power':   '#EF6C00',
  'Security':           '#C62828',
  'General':            '#455A64',
};

function humanSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileHref(id) {
  const base = process.env.REACT_APP_API_URL || '/api';
  const token = localStorage.getItem('token');
  // Auth header can't be set on plain window.open; use a data-URL friendly
  // approach — we open a fetch → blob if needed. Simplest: append token query.
  // But auth middleware reads header only. So we fetch as blob and open.
  return { base, id, token };
}

async function openGuideBlob(id, filename) {
  const token = localStorage.getItem('token');
  const base = process.env.REACT_APP_API_URL || '/api';
  const resp = await fetch(`${base}/field-guides/${id}/download`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    alert(`Failed to open: ${resp.status}`);
    return;
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function FieldGuides() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [guides, setGuides] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [q, setQ] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searching, setSearching] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState(null); // guide row or null

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/field-guides');
      setGuides(data.guides || []);
      setIsAdmin(!!data.isAdmin);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  useEffect(() => {
    if (!q || !q.trim()) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get('/field-guides/search', { params: { q } });
        setSearchResults(data.results || []);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const grouped = useMemo(() => {
    const g = {};
    for (const guide of guides) {
      const cat = guide.category || 'General';
      if (!g[cat]) g[cat] = [];
      g[cat].push(guide);
    }
    return g;
  }, [guides]);

  const orderedCategories = useMemo(() => {
    const known = CATEGORY_ORDER.filter(c => grouped[c]);
    const others = Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c));
    return [...known, ...others];
  }, [grouped]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <BookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Field Guides</Typography>
          <Typography variant="body2" color="text.secondary">
            ACS internal technical standards and field procedures. Also searchable by the ACS assistant.
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Upload Guide
          </Button>
        )}
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search field guides (title or contents)…"
          value={q}
          onChange={e => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={18} /> : <SearchIcon />}
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
      ) : searchResults !== null ? (
        <SearchResults
          results={searchResults}
          query={q}
          onOpen={openGuideBlob}
        />
      ) : (
        orderedCategories.map(cat => (
          <Box key={cat} sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Chip
                label={cat}
                sx={{ bgcolor: CATEGORY_COLORS[cat] || '#455A64', color: 'white', fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                {grouped[cat].length} guide{grouped[cat].length === 1 ? '' : 's'}
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {grouped[cat].map(g => (
                <Grid item xs={12} sm={6} md={4} key={g.id}>
                  <GuideCard
                    guide={g}
                    isAdmin={isAdmin}
                    onOpen={() => openGuideBlob(g.id, g.filename)}
                    onEdit={() => setEditing(g)}
                    onDelete={async () => {
                      if (!window.confirm(`Delete "${g.title}"? This cannot be undone.`)) return;
                      try {
                        await api.delete(`/field-guides/${g.id}`);
                        load();
                      } catch (err) {
                        alert(err.response?.data?.error || err.message);
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}

      {uploadOpen && (
        <UploadDialog
          onClose={() => setUploadOpen(false)}
          onUploaded={() => { setUploadOpen(false); load(); }}
        />
      )}

      {editing && (
        <EditDialog
          guide={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </Box>
  );
}

function GuideCard({ guide, isAdmin, onOpen, onEdit, onDelete }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <PdfIcon sx={{ color: '#C62828', mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            {guide.code && (
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {guide.code}
              </Typography>
            )}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {guide.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {humanSize(guide.size_bytes)} · uploaded {new Date(guide.uploaded_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
      <CardActions>
        <Button size="small" startIcon={<OpenIcon />} onClick={onOpen}>Open PDF</Button>
        <Box sx={{ flex: 1 }} />
        {isAdmin && (
          <>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton>
            </Tooltip>
          </>
        )}
      </CardActions>
    </Card>
  );
}

function SearchResults({ results, query, onOpen }) {
  if (!results.length) {
    return <Alert severity="info">No guides match “{query}”.</Alert>;
  }
  return (
    <Stack spacing={2}>
      {results.map(r => (
        <Paper key={r.id} sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={r.category || 'General'}
              sx={{ bgcolor: CATEGORY_COLORS[r.category] || '#455A64', color: 'white' }}
            />
            {r.code && <Chip size="small" variant="outlined" label={r.code} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{r.title}</Typography>
            <Button size="small" startIcon={<OpenIcon />} onClick={() => onOpen(r.id, r.title + '.pdf')}>
              Open PDF
            </Button>
          </Stack>
          {r.snippet && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              {r.snippet}
            </Typography>
          )}
        </Paper>
      ))}
    </Stack>
  );
}

const CATEGORY_OPTIONS = [
  'Standards & Basics',
  'AV & Networking',
  'Lighting & Power',
  'Security',
  'General',
];

function UploadDialog({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!file) { setError('Choose a PDF first'); return; }
    setBusy(true);
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    if (code) fd.append('code', code);
    if (category) fd.append('category', category);
    try {
      await api.post('/field-guides', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Field Guide</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
            {file ? file.name : 'Choose PDF'}
            <input hidden type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
          </Button>
          <Typography variant="caption" color="text.secondary">
            Title/code/category are auto-derived from the filename (e.g. "103 - ACS_Wiring_Standards.pdf" → code 103, title "ACS Wiring Standards", category "Standards &amp; Basics"). Overrides below are optional.
          </Typography>
          <Divider />
          <TextField label="Title (optional override)" value={title} onChange={e => setTitle(e.target.value)} />
          <TextField label="Code (optional override, e.g. 205)" value={code} onChange={e => setCode(e.target.value)} />
          <TextField select label="Category (optional override)" value={category} onChange={e => setCategory(e.target.value)}>
            <MenuItem value="">(auto)</MenuItem>
            {CATEGORY_OPTIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !file}>
          {busy ? 'Uploading…' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditDialog({ guide, onClose, onSaved }) {
  const [title, setTitle] = useState(guide.title || '');
  const [code, setCode] = useState(guide.code || '');
  const [category, setCategory] = useState(guide.category || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await api.put(`/field-guides/${guide.id}`, { title, code: code || null, category });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Field Guide</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Title" value={title} onChange={e => setTitle(e.target.value)} />
          <TextField label="Code" value={code} onChange={e => setCode(e.target.value)} />
          <TextField select label="Category" value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORY_OPTIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
